import React, { useCallback, useMemo, useState } from 'react'
import './styles.css'
import { Button } from 'primereact/button'
import { PersonApi } from '../../api'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { SelectButton } from 'primereact/selectbutton'
import { Loading } from '../loading/Loading'
import { authState } from '../../state/Auth.state'
import { appState } from '../../state/App.state'

// Tab 1: Forgot password – IGV number + first name + last name  → password sent by email
// Tab 2: Forgot IGV number – first name + last name + city + birthday → IGV number sent by email
//        Note: if email no longer available → use registration

type Tab = 'password' | 'igvnumber'

const TAB_OPTIONS = [
    { label: 'Passwort vergessen', value: 'password' },
    { label: 'Kennung vergessen', value: 'igvnumber' },
]

const MIN_DATE = new Date('1800-01-01')

export const ForgotPassword = () => {
    /* STATE */
    const [loading, setLoading] = useState<boolean>(false)
    const [tab, setTab] = useState<Tab>('password')

    // Tab 1: forgot password
    const [igvNumber, setIgvNumber] = useState<string>('')
    const [firstName, setFirstName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')

    // Tab 2: forgot IGV number
    const [firstNameFI, setFirstNameFI] = useState<string>('')
    const [lastNameFI, setLastNameFI] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [birthday, setBirthday] = useState<Date | null>(null)

    /* DERIVED STATE */
    const formValid = useMemo(() => {
        if (tab === 'password') {
            return igvNumber.trim().length > 0 && /^\d+$/.test(igvNumber.trim())
                && firstName.trim().length > 0 && lastName.trim().length > 0
        } else {
            return firstNameFI.trim().length > 0 && lastNameFI.trim().length > 0
                && city.trim().length > 0 && birthday != null && birthday > MIN_DATE
        }
    }, [tab, igvNumber, firstName, lastName, firstNameFI, lastNameFI, city, birthday])

    /* LOGIC */
    const onReset = useCallback(() => {
        setIgvNumber('')
        setFirstName('')
        setLastName('')
        setFirstNameFI('')
        setLastNameFI('')
        setCity('')
        setBirthday(null)
    }, [])

    const handleSubmit = useCallback(() => {
        setLoading(true)

        if (tab === 'password') {
            new PersonApi(authState.apicfg())
                .apiPersonIgvRequestGet({
                    igvKennung: parseInt(igvNumber.trim(), 10),
                    vorname: firstName.trim(),
                    nachname: lastName.trim(),
                })
                .then((ok: boolean) => {
                    if (ok) {
                        appState.addToast('Passwort wurde an Ihre hinterlegte E-Mail-Adresse gesendet.', 'success')
                        onReset()
                    } else {
                        appState.addToast('Keine passende Person gefunden. Bitte Eingaben prüfen.', 'warning')
                    }
                })
                .catch((err: unknown) => {
                    appState.addToast('Anfrage fehlgeschlagen!', 'error')
                    console.error(err)
                })
                .finally(() => setLoading(false))
        } else {
            new PersonApi(authState.apicfg())
                .apiPersonIgvRequestGet({
                    vorname: firstNameFI.trim(),
                    nachname: lastNameFI.trim(),
                    ort: city.trim(),
                    gebDat: birthday ?? undefined,
                })
                .then((ok: boolean) => {
                    if (ok) {
                        appState.addToast('Ihre IGV-Kennung wurde an Ihre hinterlegte E-Mail-Adresse gesendet.', 'success')
                        onReset()
                    } else {
                        appState.addToast('Keine passende Person gefunden. Bitte Eingaben prüfen.', 'warning')
                    }
                })
                .catch((err: unknown) => {
                    appState.addToast('Anfrage fehlgeschlagen!', 'error')
                    console.error(err)
                })
                .finally(() => setLoading(false))
        }
    }, [tab, igvNumber, firstName, lastName, firstNameFI, lastNameFI, city, birthday, onReset])

    /* TEMPLATE */
    return (
        <>
            <Loading loading={loading} />
            <div className='register-container'>
                <div className='register-form card'>
                    <h2 style={{ marginBottom: '0.5rem' }}>Zugangsdaten vergessen?</h2>

                    <SelectButton
                        value={tab}
                        options={TAB_OPTIONS}
                        onChange={(e) => { if (e.value) { setTab(e.value as Tab); onReset() } }}
                        style={{ marginBottom: '1rem', flexWrap: 'wrap' }}
                    />

                    <form
                        onSubmit={(e) => { e.preventDefault(); if (formValid) handleSubmit() }}
                        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                    >
                        {/* Tab 1: Forgot password */}
                        {tab === 'password' && (
                            <>
                                <InputText
                                    keyfilter='int'
                                    placeholder='IGV-Kennung (Nummer) *'
                                    value={igvNumber}
                                    onChange={(e) => setIgvNumber(e.target.value ?? '')}
                                />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <InputText
                                        style={{ flex: 1 }}
                                        placeholder='Vorname *'
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value ?? '')}
                                    />
                                    <InputText
                                        style={{ flex: 1 }}
                                        placeholder='Nachname *'
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value ?? '')}
                                    />
                                </div>
                            </>
                        )}

                        {/* Tab 2: Forgot IGV number */}
                        {tab === 'igvnumber' && (
                            <>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <InputText
                                        style={{ flex: 1 }}
                                        placeholder='Vorname *'
                                        value={firstNameFI}
                                        onChange={(e) => setFirstNameFI(e.target.value ?? '')}
                                    />
                                    <InputText
                                        style={{ flex: 1 }}
                                        placeholder='Nachname *'
                                        value={lastNameFI}
                                        onChange={(e) => setLastNameFI(e.target.value ?? '')}
                                    />
                                </div>
                                <InputText
                                    placeholder='Ort *'
                                    value={city}
                                    onChange={(e) => setCity(e.target.value ?? '')}
                                />
                                <Calendar
                                    placeholder='Geburtsdatum *'
                                    value={birthday}
                                    dateFormat='dd.mm.yy'
                                    onChange={(e) => {
                                        const d = e.value as Date ?? null
                                        if (d) d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
                                        setBirthday(d)
                                    }}
                                    showIcon
                                />
                                <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>
                                    Falls Ihre E-Mail-Adresse nicht mehr verfügbar ist, nutzen Sie bitte die{' '}
                                    <a href='#/register' style={{ color: 'inherit' }}>Registrierung</a>.
                                </p>
                            </>
                        )}
                    </form>

                    <Button
                        style={{ marginTop: '0.5rem' }}
                        disabled={!formValid}
                        onClick={handleSubmit}
                    >
                        {tab === 'password' ? 'Passwort zusenden' : 'Kennung zusenden'}
                    </Button>
                    <Button outlined onClick={() => (window.location.hash = '#/home')}>
                        Zurück zum Login
                    </Button>
                </div>
            </div>
        </>
    )
}
